import { useCallback } from 'react';

import { useNotification } from '@/hooks';
import { NotificationSeverity } from '@/components/notifications/Notification';
import { useDeleteCommerceMutation } from '../services/commerces.service';

interface UseDeleteCommerceProps {
  onSuccess?: () => void;
}

const useDeleteCommerce = ({ onSuccess }: UseDeleteCommerceProps = {}) => {
  const { showNotification } = useNotification();

  const [deleteCommerce, { isLoading: isDeletingCommerce }] =
    useDeleteCommerceMutation();

  const handleDeleteCommerce = useCallback(
    async (commerceId: string): Promise<void> => {
      try {
        await deleteCommerce(commerceId).unwrap();
        showNotification(
          'Comercio eliminado correctamente',
          NotificationSeverity.success
        );

        if (onSuccess) {
          onSuccess();
        }
      } catch (error: any) {
        const status = error?.status || error?.data?.status;
        const message = error?.data?.message || error?.message;

        let errorMessage = 'Error al eliminar el comercio';

        if (status === 404) {
          errorMessage = 'Comercio no encontrado';
        } else if (status === 409) {
          if (
            message?.includes('foreign key constraint') ||
            message?.includes('commerce_settings_commerce_id_fkey')
          ) {
            errorMessage =
              'No se puede eliminar el comercio porque tiene configuraciones asociadas. Primero elimina las configuraciones del comercio.';
          } else {
            errorMessage =
              'No se puede eliminar el comercio porque tiene operadores logísticos asociados. Primero desasocia los operadores logísticos.';
          }
        } else if (status === 500) {
          if (
            message?.includes('foreign key constraint') ||
            message?.includes('commerce_settings_commerce_id_fkey')
          ) {
            errorMessage =
              'No se puede eliminar el comercio porque tiene configuraciones asociadas. Primero elimina las configuraciones del comercio.';
          } else if (message?.includes('operadores logísticos')) {
            errorMessage =
              'No se puede eliminar el comercio porque tiene operadores logísticos asociados. Primero desasocia los operadores logísticos.';
          } else {
            errorMessage =
              'Error interno del servidor. Intenta nuevamente más tarde.';
          }
        } else if (status === 401) {
          errorMessage = 'Token de autorización inválido';
        } else if (message) {
          errorMessage = message;
        }

        showNotification(errorMessage, NotificationSeverity.error);
      }
    },
    [deleteCommerce, showNotification, onSuccess]
  );

  return {
    handleDeleteCommerce,
    isLoading: isDeletingCommerce,
  };
};

export default useDeleteCommerce;
